import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useConfig } from "@/context/ConfigContext";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const config = useConfig();
  const ship = subtotal >= config.free_ship_threshold ? 0 : config.ship_fee;

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-md mx-auto text-center py-32 px-5">
        <ShoppingBag className="w-12 h-12 text-[var(--olive)] mx-auto" strokeWidth={1} />
        <h1 className="font-serif text-4xl text-[var(--bronze)] mt-6">Your bag is empty</h1>
        <p className="text-[var(--olive)] mt-3">Discover kurtas woven with heritage.</p>
        <Link to="/shop" className="btn-primary inline-flex items-center gap-2 mt-8 px-8 py-4 uplabel">Start Shopping <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 lg:py-16">
      <h1 className="font-serif text-4xl sm:text-5xl text-[var(--bronze)] mb-12">Shopping Bag</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 divide-y divide-[var(--line)]">
          {items.map((it) => (
            <div key={`${it.product_id}-${it.size}`} data-testid={`cart-item-${it.product_id}`} className="flex gap-5 py-6">
              <img src={it.image} alt={it.name} className="w-24 lg:w-28 aspect-[3/4] object-cover bg-[#F3EECB]" />
              <div className="flex-1">
                <div className="flex justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl text-[var(--bronze)]">{it.name}</h3>
                    <p className="uplabel text-[0.62rem] mt-1">Size: {it.size}</p>
                  </div>
                  <button onClick={() => removeItem(it.product_id, it.size)} data-testid={`cart-remove-${it.product_id}`} className="text-[var(--olive)] hover:text-[var(--maroon)] transition-colors h-fit">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center border border-[var(--line)]">
                    <button onClick={() => updateQty(it.product_id, it.size, it.quantity - 1)} className="px-3 py-1.5 text-[var(--bronze)]"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-8 text-center text-sm text-[var(--bronze)]">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.size, it.quantity + 1)} className="px-3 py-1.5 text-[var(--bronze)]"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="font-medium text-[var(--bronze)]">{inr(it.price * it.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#F3EECB] p-8 sticky top-28">
            <h2 className="font-serif text-2xl text-[var(--bronze)] mb-6">Order Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-[var(--bronze)]"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              <div className="flex justify-between text-[var(--bronze)]"><span>Shipping</span><span>{ship === 0 ? "Free" : inr(ship)}</span></div>
            </div>
            <div className="flex justify-between font-serif text-xl text-[var(--bronze)] mt-6 pt-6 border-t border-[var(--line)]">
              <span>Total</span><span>{inr(subtotal + ship)}</span>
            </div>
            <Link to="/checkout" data-testid="checkout-btn" className="btn-primary flex items-center justify-center gap-2 uplabel py-4 mt-8">
              Proceed To Checkout <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/shop" className="block text-center uplabel text-[var(--olive)] mt-4 link-underline w-fit mx-auto">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
